// What part of the deck is on screen: follows my otter, or stays where it was dragged to; zoom (0.75x-6x) eases toward
// its target keeping the point under the cursor still. Positions on screen are cam.x + world_tiles * T * scale, with
// cam.x/cam.y whole pixels; the Phaser camera is set from this every frame. All of it in CSS pixels: the canvas may be
// finer (deck/dpr.ts), which only deck/scene.js and the pointer's way in (deck/input.js) need to know.
// "The view" below is the part of the window the page's bars don't cover (insets: deck/hud.js measures them; the top
// pills, the bottom bar and chat, the safe areas): my otter is kept in the middle of that, not of the window, so it
// never walks under the bar.
// Zoomed all the way out the whole map fits a 1440x900 window (116x62 tiles of 16 px). The view starts at 2x on a
// computer, 3x on a phone (START_ZOOM); there is no saved zoom, each visit starts there.
// Zoomed out that far there's nothing to follow: along a side the map fits, it sits in the middle of the view; along
// a side it nearly fits (up to NEAR times the view) following keeps the map's edges outside the view, still keeping
// my otter in it. Zoomed in further, the view follows me (or stays where dragged) as always. Dragged, the view goes as
// far as puts the open sea round the map (SEA_MARGIN, where one can swim) in the view, every edge, and no further.
// Once dragged, the view stays put while I click about (the otter walks; the view doesn't move). It comes back to me
// when I walk with the keys, press 回到我 (or Space), or my otter walks out toward the window's edge (EDGE); coming
// back is a glide (GLIDE), never a jump.
import { SEA_MARGIN } from './collide.js';

export const MIN_ZOOM = 0.75, MAX_ZOOM = 6;
// Snap the zoom, once it has settled, to where every sprite pixel is the same number of screen pixels: a whole zoom
// (2x, 3x, ...), or 0.75 / 1.5 below 2x. Zooming itself still eases smoothly; then it eases on to the next step.
// false: leave the zoom wherever it stops.
export const ZOOM_SNAP = true;
const SNAP_AFTER_MS = 250;
const STEPS = [0.75, 1, 1.5, 2, 3, 4, 5, 6];
/** The zoom step to settle on from z: the next one up after zooming in (dir > 0), down after zooming out (dir < 0),
 *  else the nearest; so even one wheel notch gets to the next step instead of easing back. */
export function snapZoom(z, dir = 0) {
  if (STEPS.some((s) => Math.abs(s - z) < 1e-6)) return z;
  if (dir > 0) return STEPS.find((s) => s > z) ?? MAX_ZOOM;
  if (dir < 0) return [...STEPS].reverse().find((s) => s < z) ?? MIN_ZOOM;
  return STEPS.reduce((a, b) => (Math.abs(b - z) < Math.abs(a - z) ? b : a));
}
/** The zoom step after (dir > 0) or before (dir < 0) z, even when z is a step already (the +/− buttons, + and -). */
export function nextZoom(z, dir) {
  return dir > 0 ? STEPS.find((s) => s > z + 1e-6) ?? MAX_ZOOM : [...STEPS].reverse().find((s) => s < z - 1e-6) ?? MIN_ZOOM;
}
// The zoom a visit starts at: one step out from the old 3x on a computer (artin 11-02: it felt a bit close); a phone
// (touch first, a small screen) keeps 3x, where otters and names stay big enough to read and tap.
export const START_ZOOM = { desktop: 2, phone: 3 };
const isPhone = () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 600;
const NEAR = 1.25;
// Gliding back to me: each frame the view closes this share (per second, growing as it goes, so a walking otter is
// caught up with too) of the gap to where following would put it; within a pixel it is following again.
const GLIDE = { rate: 5, gain: 14, max: 22 };
// How near the view's edge (px; more at the top for the name tag) my otter may walk while the view stays put, plus a
// tile (at most a quarter of the view); walking out past it brings the view back.
const EDGE = { side: 24, top: 56, bottom: 24 }, EDGE_TILES = 1, DRAG_QUIET_MS = 400;
// A change in the bars (the chat log opened, the bar wrapping to another row) moves the view this share a second.
const INSET_RATE = 10;
const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
let zoomedAt = 0, zoomDir = 0; // when and which way the zoom last changed by hand
export function createCamera({ tile, map = null, onFree = () => {} }) {
  const c = {
    scale: isPhone() ? START_ZOOM.phone : START_ZOOM.desktop, x: 0, y: 0, width: innerWidth, height: innerHeight,
    free: null, // {wx, wy}: the world point (tiles) at the screen's centre while the camera is not following me
    anim: null, // {target, sx, sy}: easing the scale toward target, keeping the point under (sx, sy) still; {target, mid}: around the view's centre (the snap, step())
    glide: null, // {t}: following again, easing from where the view was (t: seconds since)
    draggedAt: -1e9, // when the view was last dragged by hand (drag())
    insets: null, // {top, right, bottom, left}: how far the bars cover the window (px), easing toward setInsets()'s
  };
  let insetsTo = null;
  c.S = () => tile * c.scale; // one tile, in screen pixels
  /** The bars' covered insets (deck/hud.js): the first right away, then eased toward. */
  c.setInsets = (ins) => { insetsTo = { ...ins }; if (!c.insets) c.insets = { ...ins }; };
  /** The open view's edges on screen: {x0, y0, x1, y1, cx, cy} (cx, cy its middle). */
  c.area = () => {
    const i = c.insets ?? { top: 0, right: 0, bottom: 0, left: 0 }, x0 = i.left, x1 = c.width - i.right, y0 = i.top, y1 = c.height - i.bottom;
    // never less than 40% of the window either way (a short landscape phone): the bars then shrink in proportion
    const fix = (a, b, n) => { const k = a + b > n * 0.6 ? (n * 0.6) / (a + b) : 1; return [a * k, n - b * k]; };
    const [X0, X1] = x1 - x0 < c.width * 0.4 ? fix(i.left, i.right, c.width) : [x0, x1];
    const [Y0, Y1] = y1 - y0 < c.height * 0.4 ? fix(i.top, i.bottom, c.height) : [y0, y1];
    return { x0: X0, y0: Y0, x1: X1, y1: Y1, cx: (X0 + X1) / 2, cy: (Y0 + Y1) / 2 };
  };
  /** Follow me again: from a dragged view, a glide back (no jump). */
  c.follow = () => { if (c.free) c.glide = { t: 0 }; c.free = null; onFree(false); };
  c.pan = (wx, wy) => { c.free = { wx, wy }; c.glide = null; onFree(true); };
  /** A drag by hand (deck/input.js): pan, and don't let my walking pull the view back while the hand is on it. */
  c.drag = (wx, wy, now = performance.now()) => { c.pan(wx, wy); c.draggedAt = now; };
  /** The same, by (dwx, dwy) tiles from where the view's centre is now. */
  c.dragBy = (dwx, dwy, now) => { const p = c.centre(); c.drag(p.wx + dwx, p.wy + dwy, now); };
  /** The world point (tiles) at the view's centre now. */
  c.centre = () => { const a = c.area(); return c.free ? { wx: c.free.wx, wy: c.free.wy } : { wx: (a.cx - c.x) / c.S(), wy: (a.cy - c.y) / c.S() }; };
  /** Screen pixel -> world tiles. */
  c.toWorld = (sx, sy) => [(sx - c.x) / c.S(), (sy - c.y) / c.S()];
  /** One zoom step in (dir > 0) or out, eased, around the view's centre (the +/− buttons, the + and - keys). One press is one
   *  step: straight to the next step, no jump first and then a snap on (that read as two presses, and from 6x a press
   *  went past 5x to 4x). Pressed again while easing, it steps on from where it was headed. */
  c.step = (dir) => {
    const target = nextZoom(c.anim?.target ?? c.scale, dir);
    c.anim = target !== c.scale ? { target, mid: true } : null; // (+ then − in one frame: back where it was)
    zoomDir = 0;
  };
  /** Zoom by a factor right away, around the view's centre (pinch). */
  c.zoom = (f) => {
    const k = clampZoom(c.scale * f) / c.scale, a = c.area(); c.scale *= k;
    // the view's offsets too, right away (not at the next frame): an easing that starts before then (the snap) must
    // read the world point at the centre as it is, or the view jumps
    c.x = Math.round(a.cx - (a.cx - c.x) * k); c.y = Math.round(a.cy - (a.cy - c.y) * k);
    zoomedAt = performance.now(); zoomDir = Math.sign(f - 1);
  };
  /** Ease toward a zoom by a factor, around screen point (sx, sy) (wheel). */
  c.zoomAt = (f, sx, sy) => { c.anim = { target: clampZoom((c.anim?.target ?? c.scale) * f), sx, sy }; zoomDir = Math.sign(f - 1); };
  /** Per frame: advance the zoom, then place the view on (cx, cy) (tiles) unless it was dragged elsewhere. */
  c.update = (dt, now, me) => {
    if (insetsTo) { // ease the bars' insets (a jump would jerk the view)
      const a = Math.min(1, dt * INSET_RATE);
      for (const k of ['top', 'right', 'bottom', 'left']) { const d = insetsTo[k] - c.insets[k]; c.insets[k] = Math.abs(d) < 0.5 ? insetsTo[k] : c.insets[k] + d * a; }
    }
    let A = c.area();
    // settled somewhere between steps: ease on to the nearest one, around the view's centre
    if (ZOOM_SNAP && !c.anim && (now ?? performance.now()) - zoomedAt > SNAP_AFTER_MS && Math.abs(snapZoom(c.scale, zoomDir) - c.scale) > 1e-6) {
      c.anim = { target: snapZoom(c.scale, zoomDir), mid: true }; zoomDir = 0;
    }
    if (c.anim) {
      const { target, mid } = c.anim, sx = mid ? A.cx : c.anim.sx, sy = mid ? A.cy : c.anim.sy, S0 = c.S(), wx = (sx - c.x) / S0, wy = (sy - c.y) / S0;
      c.scale = Math.abs(target - c.scale) < 0.002 ? target : c.scale + (target - c.scale) * Math.min(1, dt * 16);
      if (c.scale === target) { c.anim = null; zoomedAt = 0; }
      const S1 = c.S(); // put the same world point back under the cursor; following me around the view's centre: still following
      if (c.free || !mid) c.pan((A.cx - (sx - wx * S1)) / S1, (A.cy - (sy - wy * S1)) / S1);
    }
    const S = c.S();
    const cx = c.free ? c.free.wx : me ? me.x + 0.5 : null, cy = c.free ? c.free.wy : me ? me.y + 0.5 : null;
    if (cx === null) return;
    const tx = fit(Math.round(A.cx - cx * S), A.x0, A.x1, map?.width, !c.free && me ? me.x + 0.5 : null, map?.x0 ?? 0);
    const ty = fit(Math.round(A.cy - cy * S), A.y0, A.y1, map?.height, !c.free && me ? me.y + 0.5 : null, map?.y0 ?? 0);
    // dragged past where the view may go: the view's centre is where it stopped (a drag back, deck/input.js dragBy,
    // moves it at once). A dragged view stays on the same world point at the view's centre, so a change in the bars
    // slides it by half the change, as it does a followed one.
    // (only on an axis it stopped on: the offsets are whole pixels, and a slow drag's fractions must add up)
    if (c.free && tx !== Math.round(A.cx - cx * S)) c.free.wx = (A.cx - tx) / S;
    if (c.free && ty !== Math.round(A.cy - cy * S)) c.free.wy = (A.cy - ty) / S;
    if (c.glide) {
      c.glide.t += dt;
      const a = Math.min(1, dt * Math.min(GLIDE.max, GLIDE.rate + GLIDE.gain * c.glide.t));
      const step = (from, to) => { const d = to - from, k = Math.round(d * a); return Math.abs(d) <= 1 ? to : from + (k || Math.sign(d)); };
      c.x = step(c.x, tx); c.y = step(c.y, ty);
      if (c.x === tx && c.y === ty) c.glide = null;
    } else { c.x = tx; c.y = ty; }
    // dragged away, and my otter walks out toward the view's edge (not while the hand is still on the view): come
    // back. Walking in from beyond the edge doesn't count (where it's headed is on screen).
    const wx = me ? (me.drawX ?? me.x) + 0.5 : 0, wy = me ? (me.drawY ?? me.y) + 0.5 : 0, last = c.lastMe;
    c.lastMe = me ? { wx, wy } : null;
    if (c.free && me && me.moving && last && (now ?? performance.now()) - c.draggedAt > DRAG_QUIET_MS) {
      const px = c.x + wx * S, py = c.y + wy * S, vx = wx - last.wx, vy = wy - last.wy, w = A.x1 - A.x0, h = A.y1 - A.y0;
      const m = (e, view) => Math.min(view / 4, e + EDGE_TILES * S);
      if ((px < A.x0 + m(EDGE.side, w) && vx < 0) || (px > A.x1 - m(EDGE.side, w) && vx > 0) ||
          (py < A.y0 + m(EDGE.top, h) && vy < 0) || (py > A.y1 - m(EDGE.bottom, h) && vy > 0)) c.follow();
    }
  };
  /** One axis: the offset `at` (px) the view wants, kept so the view (v0..v1 on screen) goes no further than the open
   *  sea round the map (`tiles` long); if the map fits the view, centred in it; if it nearly fits and the view follows
   *  me (`mine`: my otter's world position), its edges kept outside the view. Following, my otter is kept in the view
   *  with a tile of room around it (out at sea past the map's edge too). */
  function fit(at0, v0, v1, tiles, mine0, origin = 0) {
    if (!tiles) return at0;
    // the map runs from `origin` (a grown map's x0 / y0, 0 or less) for `tiles`: work from its first tile, then back
    const at = at0 + origin * c.S(), mine = mine0 === null ? null : mine0 - origin;
    const S = c.S(), span = tiles * S, view = v1 - v0, sea = SEA_MARGIN * S;
    const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
    let x = span <= view ? v0 + (view - span) / 2
      : mine !== null && span <= view * NEAR ? clamp(at, v1 - span, v0)
      : clamp(at, v1 - span - sea, v0 + sea);
    if (mine !== null) x = clamp(x, v0 + S - mine * S, v1 - S - mine * S);
    return Math.round(x - origin * S);
  }
  /** Whether the view is pinned to the map (zoomed out so far it fits, or nearly) rather than following, per axis. */
  c.fitted = () => { const a = c.area(); return { x: !!map && map.width * c.S() <= (a.x1 - a.x0) * NEAR, y: !!map && map.height * c.S() <= (a.y1 - a.y0) * NEAR }; };
  return c;
}
